import React, { useEffect } from 'react';
import { useRouteError, isRouteErrorResponse } from 'react-router-dom';
import { useClerk, useAuth } from '@clerk/clerk-react';
import pig1 from '../../assets/graphics/pig1.gif';
import { isAuthError } from '../utils/isAuthError';

/**
 * Error boundary for React Router routes.
 * Catches errors thrown during route rendering (including Convex validation errors).
 */
export const RouteErrorBoundary: React.FC = () => {
  const error = useRouteError();
  const { signOut, redirectToSignIn } = useClerk();
  const { isSignedIn } = useAuth();

  // Check if this is a Convex validation error
  const errorMessage = error instanceof Error ? error.message : String(error);
  const isValidationError =
    errorMessage?.includes('ArgumentValidationError') ||
    errorMessage?.includes('Value does not match validator');

  const authError = isAuthError(errorMessage);

  // Auto-redirect on auth errors — don't show an error dialog
  useEffect(() => {
    if (!authError) return;
    const returnPath = window.location.pathname + window.location.search;
    sessionStorage.setItem('auth_return_path', returnPath);

    if (isSignedIn) {
      signOut({ redirectUrl: window.location.origin });
    } else {
      redirectToSignIn();
    }
  }, [authError, isSignedIn, signOut, redirectToSignIn]);

  // Check if this is a 404 response
  const is404 = isRouteErrorResponse(error) && error.status === 404;

  // While redirecting on auth error, show nothing (redirect is in progress)
  if (authError) {
    return null;
  }

  // For validation errors or 404s, show the 404 page
  if (isValidationError || is404) {
    return (
      <section className="min-h-screen flex flex-col items-center justify-center bg-background px-6 py-12">
        <div className="bg-card border-2 border-border/50 rounded-3xl p-8 md:p-12 shadow-lg text-center max-w-lg">
          <h1 className="text-8xl md:text-9xl font-bold text-primary/20 mb-4">
            404
          </h1>
          <div className="flex justify-center mb-6">
            <img
              src={pig1}
              alt="Gruntled mascot"
              className="w-48 h-48 md:w-60 md:h-60 object-contain"
            />
          </div>
          <h2 className="text-2xl md:text-3xl font-semibold text-foreground mb-4">
            Page Not Found
          </h2>
          <p className="text-muted-foreground mb-8 max-w-md mx-auto">
            The page you're looking for doesn't exist or the link is invalid.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => window.history.back()}
              className="px-6 py-3 bg-secondary text-secondary-foreground font-medium rounded-full hover:bg-secondary/80 transition-colors"
            >
              Go Back
            </button>
            <button
              onClick={() => window.location.href = '/'}
              className="px-6 py-3 bg-primary text-primary-foreground font-medium rounded-full hover:bg-primary/90 transition-colors flex items-center justify-center gap-2"
            >
              <span>Go Home</span>
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 12 8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
              </svg>
            </button>
          </div>
        </div>
      </section>
    );
  }

  // For other errors, show a generic error page
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="bg-white p-8 rounded-lg shadow-lg max-w-md text-center">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">
          Something went wrong
        </h2>
        <p className="text-gray-600 mb-6">
          {errorMessage || 'An unexpected error occurred.'}
        </p>
        <div className="flex gap-4 justify-center">
          <button
            onClick={() => window.location.reload()}
            className="bg-gray-200 text-gray-800 px-6 py-2 rounded-md hover:bg-gray-300 transition-colors"
          >
            Try Again
          </button>
          <button
            onClick={() => window.location.href = '/'}
            className="bg-primary-5 text-white px-6 py-2 rounded-md hover:bg-primary-6 transition-colors"
          >
            Go Home
          </button>
        </div>
      </div>
    </div>
  );
};
